<template>
  <div class="navbar f fc fbt">
    <div class="left f fc">
      <el-icon size="20" style="margin-right: 10px" @click="store.setSystemSetting('hideMenu')">
        <Expand v-if="systemSetting.hideMenu" />
        <Fold v-else />
      </el-icon>
      <div>欢迎登录，{{ userName }}</div>
    </div>
    <div class="right f fc">
      <el-popover placement="bottom-start" :width="80" trigger="hover" :content="msgTip">
        <template #reference>
          <el-badge :value="unreadCount" class="item" :hidden="unreadCount === 0">
            <BellOutlined @click="openMessageCenter" class="bell" />
          </el-badge>
        </template>
      </el-popover>
      <div class="btn" @click="gotoNew" v-show="showNewEntry">体验新版</div>
      <div class="btn" @click="logout">登出</div>
    </div>
    <MessageCenter ref="MessageCenterRef" />
    <MessageNotice ref="MessageNoticeRef" />
  </div>
</template>

<script setup>
import { rootStore } from '@/store/rootStore';
import { storeToRefs } from 'pinia';
import { BellOutlined } from '@ant-design/icons-vue';
import { Expand, Fold } from '@element-plus/icons-vue';
import { ElNotification, ElMessage } from 'element-plus';
import { gotoNew, getVersion } from '@/lib/version';
import { computed, ref, onMounted, onUnmounted, h } from 'vue';
import { useRoute } from 'vue-router';
import { debounce } from 'lodash';
import { logout } from '@/lib/auth';
import request from '@/lib/request';
import apiPath from '@/lib/apiPath';
import { BACKEND_ID } from '@/lib/utils';
import { getStore } from '@/lib/localStore';
import MessageCenter from '../MessageCenter/MessageCenter.vue';
import MessageNotice from '../MessageCenter/MessageNotice.vue';

const route = useRoute();

const showNewEntry = computed(() => getVersion() !== '/v2' && route.path === '/');
const store = rootStore();

const { userName, unreadCount, systemSetting } = storeToRefs(store);

const isPageVisible = ref(true);
const msgTip = computed(() => (unreadCount.value ? '您有新的消息，可点击查看' : '暂无新消息'));
const getUnreadCount = () => {
  if (isPageVisible.value) {
    request
      .get(apiPath.notification.getUnreadCount, {
        backend_id: BACKEND_ID,
      })
      .then((res) => {
        store.setUnreadList(res);
      });
  }
};

const MessageCenterRef = ref();
const openMessageCenter = () => {
  MessageCenterRef.value.initDrawer();
};

const MessageNoticeRef = ref();
const getMessageList = async (type = 1) => {
  return request
    .get(apiPath.notification.message, {
      type,
      page: 1,
      page_size: 1000,
      backend_id: BACKEND_ID,
    })
    .then((res) => {
      const list = res?.list || [];
      return list.filter((item) => item.is_read === 0);
    });
};
const showMessage = async () => {
  const noticeVisible = MessageNoticeRef.value?.getDialogVisible() ?? false;
  if (!noticeVisible) {
    const systemList = await getMessageList(1); // 获取公告列表
    if (systemList?.length) {
      MessageNoticeRef.value.initModal(systemList, 'list');
    } else {
      if (siteMsgFlag.value) {
        const siteList = await getMessageList(2); // 获取站内信息列表
        if (siteList?.length) {
          showSiteMsg(siteList);
        }
      }
    }
  }
};
const getMessageDetail = (id) => {
  request.get(`${apiPath.notification.message}/${id}`);
};
const noRemindMsg = debounce((id) => {
  request.post(`${apiPath.notification.message}/${id}/no_reminder`).then(() => {
    ElMessage.success('设置成功');
    reportLogs('$Click', {
      $module: `用户拒收消息：${id}`,
    });
  });
}, 500);
const openNotification = (data) => {
  getMessageDetail(data.id);
  ElNotification({
    type: 'info',
    customClass: 'custom-notification-class',
    dangerouslyUseHTMLString: false, // 使用 VNode 渲染，不需要 HTML 字符串
    title: data.title,
    message: h('div', [
      h('div', { class: 'editor-content-view', innerHTML: data.content }),
      h(
        'div',
        {
          style: 'cursor: pointer; color: blue;',
          onClick: () => noRemindMsg(data.id),
        },
        '不再接收此通知'
      ),
    ]),
    duration: 8000,
  });
};

const siteMsgFlag = ref(true);
const showSiteMsg = (list) => {
  let index = 0;
  siteMsgFlag.value = false;
  // 直接执行第一次
  if (index < list.length) {
    openNotification(list[index]);
    index++;
  }
  // 设置定时器，执行后续任务
  const intervalId = setInterval(() => {
    if (index < list.length) {
      openNotification(list[index]);
      index++;
    } else {
      clearInterval(intervalId); // 清除定时器
      siteMsgFlag.value = true;
    }
  }, 8000);
};

// 定时任务
let intervalTasks = [];
const startIntervalTask = (callback, interval) => {
  const id = setInterval(callback, interval);
  intervalTasks.push(id);
  return id;
};
const stopAllIntervals = () => {
  intervalTasks.forEach(clearInterval);
  intervalTasks = [];
};
const handleVisibilityChange = () => {
  if (document.hidden) {
    stopAllIntervals();
  } else {
    startTasks(); // 恢复所有定时任务
  }
};
const startTasks = () => {
  startIntervalTask(getUnreadCount, 10 * 1000); // 每10秒调用一次
  startIntervalTask(showMessage, 10 * 1000); // 每10秒调用一次
};
const getSetting = () => {
  const settingInfo = getStore({ name: 'systemSetting' });
  if (settingInfo !== null) {
    store.setSystemSetting('hideMenu', settingInfo.hideMenu);
  }
};

onMounted(() => {
  getUnreadCount(); // 初始调用
  showMessage();
  startTasks();
  document.addEventListener('visibilitychange', handleVisibilityChange);
  getSetting();
});
onUnmounted(() => {
  stopAllIntervals();
  document.removeEventListener('visibilitychange', handleVisibilityChange);
});
</script>

<style lang="less" scoped>
.navbar {
  height: 50px;
  box-sizing: border-box;
  font-size: 14px;

  .btn {
    margin-left: 30px;
    cursor: pointer;
  }

  .bell {
    font-size: 18px;
  }
}
</style>
<style>
.custom-notification-class {
  max-height: 400px;
  overflow-y: auto !important;
}
.sgp-link {
  margin-left: 20px;
}
</style>
